import React, { useRef, useCallback, useEffect } from "react";
import { DivInput, FormControlInput, FormGroupInput, LabelInput } from "./styles";
import { formatarMoeda2 } from "../../contents/VariaveisGlobais";

const InputGeral = ({
  tipo,
  label,
  placeholder,
  value,
  onChange,
  max,
  min,
  disabled = false,
  upperCase = true,
  step = 1,
  decimal = 2,
  bindItem,
  field,
  idComponente,
  handleBlur,
  rowSpace = 6,
  valueNoUpdate,
  styleComponent,
  error // <-- adicionado: opcional para acessibilidade
}) => {
  const inputRef = useRef(null);

  const formatNumber = useCallback(
    (num) => formatarMoeda2(num, decimal),
    [decimal]
  );

  // ====== Helpers (mantidos) ======
  const countDigits = (s = "") => (String(s).match(/\d/g) || []).length;
  const caretFromDigitIndex = (formatted, digitIdx) => {
    if (digitIdx <= 0) return 0;
    let i = 0, seen = 0;
    while (i < formatted.length) {
      if (/\d/.test(formatted[i])) {
        seen++;
        if (seen === digitIdx) return i + 1;
      }
      i++;
    }
    return formatted.length;
  };
  const findDecimalSepPos = (s) => {
    const posComma = s.indexOf(",");
    if (posComma >= 0) return posComma;
    const posDot = s.indexOf(".");
    return posDot;
  };

  const handleChange = useCallback(
    (e) => {
      let newValue = e.target.value;

      if (tipo === "caixaSelecao") {
        newValue = e.target.checked;
      }

      if (tipo === "monetario") {
        const rawBefore = e.target.value;
        const caretBefore = e.target.selectionStart ?? 0;
        const digitsLeft = countDigits(rawBefore.slice(0, caretBefore));
        const justTypedSeparator = /[,.]/.test(rawBefore[caretBefore - 1] || "");
        const formattedValue = formatNumber(rawBefore || "0");
        newValue = formattedValue;

        if (bindItem && field in bindItem) bindItem[field] = newValue;
        onChange?.({ target: { value: newValue } }, newValue);

        requestAnimationFrame(() => {
          try {
            const f = inputRef.current;
            if (!f) return;
            const finalStr = f.value ?? newValue;
            if (justTypedSeparator) {
              const sepPos = findDecimalSepPos(finalStr);
              if (sepPos >= 0) {
                const next = sepPos + 1;
                f.setSelectionRange(next, next);
                return;
              }
            }
            const nextPos = caretFromDigitIndex(finalStr, digitsLeft);
            f.setSelectionRange(nextPos, nextPos);
          } catch {}
        });
        return;
      }

      if (tipo === "date" && min && e.target.value) {
        const selectedDate = new Date(e.target.value);
        const maxDate = min ? new Date(min) : null;
        if (selectedDate < maxDate) e.target.value = min;
      }

      if (upperCase && tipo !== "caixaSelecao" && tipo !== "monetario") {
        e.target.value = e.target.value.toUpperCase();
        newValue = e.target.value;
      }

      if (bindItem && field in bindItem) bindItem[field] = newValue;
      onChange?.(e, newValue);
    },
    [tipo, bindItem, field, onChange, formatNumber, min, upperCase]
  );

  const onBlurEvent = useCallback(() => {
    if (typeof handleBlur === "function") handleBlur();
  }, [handleBlur]);

  // 🔹 ID do erro e aria
  const errorId = error ? `${idComponente}-erro` : undefined;

  const common = {
    id: idComponente,
    placeholder,
    onChange: handleChange,
    onBlur: onBlurEvent,
    disabled,
    "aria-label": label || placeholder,
    "aria-invalid": !!error,
    "aria-describedby": errorId,
    ...(styleComponent && { style: styleComponent }),
    value: value !== undefined ? value : bindItem?.[field],
  };

  const renderInput = () => {
    switch (tipo) {
      case "text":
        return <FormControlInput type="text" maxLength={max} {...common} />;
      case "email":
        return <FormControlInput type="email" maxLength={max || 254} {...common} />;
      case "textArea":
        return (
          <FormControlInput
            as="textarea"
            rows={rowSpace}
            style={{ width: "100%" }}
            {...common}
          />
        );
      case "numero":
        return <FormControlInput type="number" step={step} ref={inputRef} {...common} />;
      case "monetario":
        return (
          <FormControlInput
            type="text"
            inputMode="decimal"
            ref={inputRef}
            {...common}
          />
        );
      case "date":
        return <FormControlInput type="date" min={min} max={max} {...common} />;
      case "hora":
        return <FormControlInput type="time" {...common} />;
      case "caixaSelecao":
        return (
          <input
            type="checkbox"
            checked={value !== undefined ? value : !!bindItem?.[field]}
            onChange={handleChange}
            onBlur={onBlurEvent}
            disabled={disabled}
            aria-label={label || placeholder}
            {...(styleComponent && { style: styleComponent })}
            id={idComponente}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (valueNoUpdate && inputRef.current && (tipo === "numero" || tipo === "monetario")) {
      if (value === undefined && bindItem?.[field] === undefined) {
        inputRef.current.value = valueNoUpdate;
      }
    }
  }, []); // eslint-disable-line

  return (
    <FormGroupInput>
      {label && (
        <LabelInput htmlFor={idComponente} className="input-group-text">
          {label}
        </LabelInput>
      )}
      <DivInput>
        {renderInput()}
        {error && (
          <div id={errorId} role="alert" style={{ color: "red", marginTop: 4 }}>
            {error}
          </div>
        )}
      </DivInput>
    </FormGroupInput>
  );
};

export default InputGeral;
